import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { CreatePurchaseOrderRequest, ReceivePurchaseOrderRequest } from '@/api/purchasing';
import type { PagedResult } from '@/api/client';
import type { PurchaseOrder } from '@/api/types';

export function usePurchaseOrders(status?: string, page = 1, limit = 30) {
  return useQuery({
    queryKey: ['purchase-orders', { status, page, limit }],
    queryFn: () =>
      api.get<PagedResult<PurchaseOrder>>('/api/purchase-orders', {
        status: status || undefined,
        page,
        limit,
      }),
  });
}

function useInvalidatePurchaseOrders() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['medicines'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useCreatePurchaseOrder() {
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: (request: CreatePurchaseOrderRequest) =>
      api.post<PurchaseOrder>('/api/purchase-orders', request),
    onSuccess: invalidate,
  });
}

export function useReceivePurchaseOrder() {
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ReceivePurchaseOrderRequest }) =>
      api.put<PurchaseOrder>(`/api/purchase-orders/${id}/receive`, request),
    onSuccess: invalidate,
  });
}

export function useCancelPurchaseOrder() {
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: (id: string) => api.put<PurchaseOrder>(`/api/purchase-orders/${id}/cancel`),
    onSuccess: invalidate,
  });
}
